import { Box, Typography, useTheme } from '@mui/material';
import * as echarts from 'echarts';
import { ReactElement, useEffect, useRef } from 'react';
import { WithListContext, useStore } from 'react-admin';
import type { ReportGranularity } from '../../../../Components/ReportsWidget/utils';
import type { ClicksSummariesResource } from '../../../../Entities/ClicksSummary';
import { formatReportDate } from './utils';

interface SeriesConfig {
  key: keyof NonNullable<ClicksSummariesResource['attributes']>;
  label: string;
}

const series: SeriesConfig[] = [
  { key: 'total_clicks', label: 'Total' },
  { key: 'getprice_clicks', label: 'Getprice' },
  { key: 'future_clicks', label: 'Future' },
  { key: 'other_clicks', label: 'Others' },
];

const getpriceColor = '#FF2300';
const futureColor = '#7C83FF';
const legendStorageKey = 'preferences.clicksSummaryChart.selectedSeries';
export const defaultClicksSummaryChartZoomState: ClicksSummaryChartZoomState = {
  start: 0,
  end: 100,
};

export interface ClicksSummaryChartZoomState {
  start: number;
  end: number;
}

interface DataZoomEventPayload {
  start?: number;
  end?: number;
  batch?: DataZoomEventPayload[];
}

function valueOf(
  record: ClicksSummariesResource,
  key: keyof NonNullable<ClicksSummariesResource['attributes']>,
): number {
  const value = record.attributes?.[key];
  return typeof value === 'number' ? value : 0;
}

function isDataZoomEventPayload(value: unknown): value is DataZoomEventPayload {
  return typeof value === 'object' && value !== null;
}

interface ChartProps {
  data: ClicksSummariesResource[];
  granularity: ReportGranularity;
  zoomState: ClicksSummaryChartZoomState;
  onZoomStateChange: (zoomState: ClicksSummaryChartZoomState) => void;
}

function Chart({ data, granularity, zoomState, onZoomStateChange }: ChartProps): ReactElement {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [selectedLegend, setSelectedLegend] = useStore<Record<string, boolean> | undefined>(
    legendStorageKey,
    undefined,
  );
  const selectedLegendRef = useRef<Record<string, boolean> | undefined>(selectedLegend);
  const zoomStateRef = useRef<ClicksSummaryChartZoomState>(zoomState);
  const theme = useTheme();

  useEffect(() => {
    selectedLegendRef.current = selectedLegend;
  }, [selectedLegend]);

  useEffect(() => {
    zoomStateRef.current = zoomState;
  }, [zoomState]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const chart = echarts.init(chartRef.current, theme.palette.mode);
    chart.setOption({
      color: [theme.palette.primary.main, getpriceColor, futureColor, theme.palette.grey[700]],
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        top: 0,
        selectedMode: 'multiple',
        data: series.map((s) => s.label),
        selected: selectedLegendRef.current,
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        top: 56,
        right: 24,
        bottom: 80,
        left: 56,
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          start: zoomStateRef.current.start,
          end: zoomStateRef.current.end,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: 16,
          height: 28,
          filterMode: 'none',
          start: zoomStateRef.current.start,
          end: zoomStateRef.current.end,
          textStyle: {
            color: theme.palette.text.secondary,
          },
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data.map((record) => formatReportDate(record.attributes?.date, granularity)),
        axisLabel: {
          color: theme.palette.text.secondary,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: {
          color: theme.palette.text.secondary,
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
      },
      series: series.map((s) => ({
        name: s.label,
        type: 'line',
        smooth: true,
        symbolSize: 5,
        data: data.map((record) => valueOf(record, s.key)),
      })),
    });
    chart.on('legendselectchanged', (event) => {
      const selected = (event as { selected?: Record<string, boolean> }).selected;
      if (!selected) {
        return;
      }

      selectedLegendRef.current = selected;
      setSelectedLegend(selected);
    });
    chart.on('datazoom', (event) => {
      if (!isDataZoomEventPayload(event)) {
        return;
      }

      const zoomEvent =
        'batch' in event && Array.isArray(event.batch) && event.batch.length > 0
          ? event.batch[0]
          : event;
      const start = typeof zoomEvent.start === 'number' ? zoomEvent.start : undefined;
      const end = typeof zoomEvent.end === 'number' ? zoomEvent.end : undefined;

      if (start == null || end == null) {
        return;
      }

      const nextZoomState = { start, end };
      zoomStateRef.current = nextZoomState;
      onZoomStateChange(nextZoomState);
    });

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [data, granularity, onZoomStateChange, setSelectedLegend, theme]);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box ref={chartRef} sx={{ height: 360, width: '100%' }} />
    </Box>
  );
}

interface ClicksSummaryChartProps {
  granularity: ReportGranularity;
  zoomState: ClicksSummaryChartZoomState;
  onZoomStateChange: (zoomState: ClicksSummaryChartZoomState) => void;
}

export default function ClicksSummaryChart({
  granularity,
  zoomState,
  onZoomStateChange,
}: ClicksSummaryChartProps): ReactElement {
  return (
    <WithListContext<ClicksSummariesResource>
      empty={<Typography sx={{ py: 2 }}>No chart data for this date range.</Typography>}
      render={({ data }) => (
        <Chart
          data={[...(data ?? [])].sort((left, right) =>
            formatReportDate(left.attributes?.date, granularity).localeCompare(
              formatReportDate(right.attributes?.date, granularity),
            ),
          )}
          granularity={granularity}
          onZoomStateChange={onZoomStateChange}
          zoomState={zoomState}
        />
      )}
    />
  );
}
